import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { MoreHorizontalIcon } from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '@/components/LoadingSpinner';
import { fetchEstadosIBGE, fetchMunicipiosIBGE, ibgeKeys } from '@/services/ibgeService';
import { createUnidade, fetchUnidades, unidadeKeys, updateUnidade } from '@/services/unidadeService';
import { parseApiError } from '@/utils/apiError';
import { formatCNPJ, unformatCNPJ } from '@/utils/helpers';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { unidadeFormSchema, unidadesSearchSchema, type TUnidadeFormData, type TUnidadesSearchFormData } from './schema';
import type { IEstadoIBGE, IMunicipioIBGE } from '@/types/estabelecimento';
import type { IUnidade, IUnidadePayload } from '@/types/unidade';

export const Route = createFileRoute('/_authenticated/admin/unidades/')({
  component: Unidades,
});

const columns: Array<{ key: keyof IUnidade; label: string }> = [
  { key: 'nome', label: 'Nome' },
  { key: 'mapa_estabelecimento_numero', label: 'Mapa Estabelecimento Número' },
  { key: 'mapa_estabelecimento_ambito', label: 'Mapa Estabelecimento Âmbito' },
  { key: 'mapa_estabelecimento_cnpj', label: 'Mapa Estabelecimento CNPJ' },
  { key: 'mapa_estabelecimento_uf', label: 'Mapa Estabelecimento UF' },
  { key: 'nome_municipio_ibge', label: 'Nome Mapa Estabelecimento Município IBGE' },
  { key: 'mapa_estabelecimento_municipio_ibge', label: 'Código Mapa Estabelecimento Município IBGE' },
  { key: 'mapa_username', label: 'Mapa Username' },
  { key: 'mapa_password', label: 'Mapa Password' },
  { key: 'smtp_host', label: 'SMTP Host' },
  { key: 'smtp_port', label: 'SMTP Port' },
  { key: 'smtp_username', label: 'SMTP Username' },
  { key: 'smtp_password', label: 'SMTP Password' },
  { key: 'email_from', label: 'Email From' },
  { key: 'smtp_use_tls', label: 'SMTP Use TLS' },
  { key: 'ativo', label: 'Ativo' },
];

const emptyUnit: TUnidadeFormData = {
  nome: '',
  mapa_estabelecimento_numero: '',
  mapa_estabelecimento_ambito: '' as TUnidadeFormData['mapa_estabelecimento_ambito'],
  mapa_estabelecimento_cnpj: '',
  mapa_estabelecimento_uf: '',
  mapa_estabelecimento_municipio_ibge: '',
  mapa_username: '',
  mapa_password: '',
  smtp_host: '',
  smtp_port: '',
  smtp_username: '',
  smtp_password: '',
  email_from: '',
  smtp_use_tls: true,
};

function formatValue(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
}

function Unidades() {
  const queryClient = useQueryClient();

  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(50);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Portal Combobox inside dialog so modal={true} does not block pointer events
  const [dialogContainer, setDialogContainer] = useState<HTMLDivElement | null>(null);
  const isEditing = editingId !== null;

  const { control: searchControl, register: registerSearch } = useForm<TUnidadesSearchFormData>({
    resolver: zodResolver(unidadesSearchSchema),
    defaultValues: { search: '' },
  });
  const search = useWatch({ control: searchControl, name: 'search' });

  // Backend ignores skip/limit/search — fetch full list once, paginate/filter client-side
  const listParams = { skip: 0, limit: 0, search: '' };
  const { data: allItems = [], isLoading, isError, error } = useQuery({
    queryKey: unidadeKeys.list(listParams),
    queryFn: () => fetchUnidades(listParams),
  });

  const filteredItems = useMemo(() => {
    const q = search?.trim().toLowerCase() ?? '';
    if (!q) return allItems;
    return allItems.filter((item) => {
      const nome = (item.nome || '').toLowerCase();
      const numero = String(item.mapa_estabelecimento_numero || '').toLowerCase();
      return nome.includes(q) || numero.includes(q);
    });
  }, [allItems, search]);

  const items = useMemo(
    () => filteredItems.slice(skip, skip + limit),
    [filteredItems, skip, limit],
  );

  const searchField = registerSearch('search');

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isValid },
  } = useForm<TUnidadeFormData>({
    resolver: zodResolver(unidadeFormSchema),
    mode: 'onChange',
    defaultValues: emptyUnit,
  });
  const codUf = useWatch({ control, name: 'mapa_estabelecimento_uf' });
  const codMunicipioIbge = useWatch({ control, name: 'mapa_estabelecimento_municipio_ibge' });
  const [nomeMunicipioFallback, setNomeMunicipioFallback] = useState<string | null>(null);

  const { data: estadosIBGE = [], isLoading: estadosLoading, isError: estadosError } = useQuery({
    queryKey: ibgeKeys.estados,
    queryFn: fetchEstadosIBGE,
  });

  const { data: municipiosIBGE = [], isLoading: municipiosLoading, isError: municipiosError } = useQuery({
    queryKey: ibgeKeys.municipios(codUf),
    queryFn: () => fetchMunicipiosIBGE(codUf),
    enabled: Boolean(codUf),
  });

  const createMutation = useMutation({ mutationFn: createUnidade });
  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: IUnidadePayload }) => updateUnidade(id, payload),
  });
  const saving = createMutation.isPending || updateMutation.isPending;

  const openCreate = () => {
    setEditingId(null);
    setNomeMunicipioFallback(null);
    reset(emptyUnit);
    setShowForm(true);
  };

  const openEdit = (item: IUnidade) => {
    setEditingId(item.id);
    setNomeMunicipioFallback(item.nome_municipio_ibge ?? null);
    reset({
      nome: item.nome || '',
      mapa_estabelecimento_numero: item.mapa_estabelecimento_numero || '',
      mapa_estabelecimento_ambito: (item.mapa_estabelecimento_ambito === 'SIF' || item.mapa_estabelecimento_ambito === 'ER'
        ? item.mapa_estabelecimento_ambito
        : '') as TUnidadeFormData['mapa_estabelecimento_ambito'],
      mapa_estabelecimento_cnpj: formatCNPJ(item.mapa_estabelecimento_cnpj || ''),
      mapa_estabelecimento_uf: item.mapa_estabelecimento_uf || '',
      mapa_estabelecimento_municipio_ibge: item.mapa_estabelecimento_municipio_ibge || '',
      mapa_username: item.mapa_username || '',
      mapa_password: item.mapa_password || '',
      smtp_host: item.smtp_host || '',
      smtp_port: item.smtp_port ? String(item.smtp_port) : '',
      smtp_username: item.smtp_username || '',
      smtp_password: item.smtp_password || '',
      email_from: item.email_from || '',
      smtp_use_tls: typeof item.smtp_use_tls === 'boolean' ? item.smtp_use_tls : true,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditingId(null);
    reset(emptyUnit);
  };

  const onSubmit = async (data: TUnidadeFormData) => {
    try {
      const payload: IUnidadePayload = {
        ...data,
        mapa_estabelecimento_cnpj: unformatCNPJ(data.mapa_estabelecimento_cnpj),
        smtp_host: data.smtp_host.trim() || null,
        smtp_port: data.smtp_port ? Number(data.smtp_port) : null,
        smtp_username: data.smtp_username.trim() || null,
        smtp_password: data.smtp_password || null,
        email_from: data.email_from.trim() || null,
        smtp_use_tls: data.smtp_use_tls ?? null,
      };

      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }

      closeForm();
      await queryClient.invalidateQueries({ queryKey: unidadeKeys.all });
    } catch (err) {
      toast.error(parseApiError(err, editingId ? 'Erro ao atualizar unidade' : 'Erro ao criar unidade'));
    }
  };

  const atEnd = skip + limit >= filteredItems.length;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">Unidades</h1>
        <p className="text-on-surface-variant">Gerencie as unidades do sistema.</p>
      </div>

      <div className="rounded-lg border border-outline-variant">
        <div className="flex flex-col gap-4 border-b border-outline-variant p-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-on-surface">Lista de Unidades</h2>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              className="w-64"
              placeholder="Buscar unidades…"
              autoComplete="off"
              {...searchField}
              onChange={(e) => {
                void searchField.onChange(e);
                setSkip(0);
              }}
            />
            <FieldLabel
              htmlFor="unitsLimit"
              className="text-sm text-on-surface-variant"
            >
              Limite
            </FieldLabel>
            <Select
              value={String(limit)}
              onValueChange={(value) => {
                setLimit(Number(value));
                setSkip(0);
              }}
            >
              <SelectTrigger id="unitsLimit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {[25, 50, 100, 200].map((size) => (
                    <SelectItem
                      key={size}
                      value={String(size)}
                    >
                      {size}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            <Button
              className="bg-success text-on-primary hover:bg-success/90"
              type="button"
              onClick={openCreate}
            >
              Nova Unidade
            </Button>
          </div>
        </div>

        <div className="p-4">
          {isError && (
            <div
              role="alert"
              aria-live="polite"
              className="mb-4 rounded-lg bg-error-container p-4 text-on-error-container"
            >
              {parseApiError(error, 'Erro ao listar unidades')}
            </div>
          )}
          {isLoading && (
            <LoadingSpinner
              text="Carregando..."
              size="small"
            />
          )}
          {!isLoading && (
            <>
              <div className="block max-w-full overflow-x-auto overflow-y-hidden">
                <table className="w-max min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-outline-variant text-left">
                      {columns.map((column) => (
                        <th
                          key={column.key}
                          className="whitespace-nowrap p-3"
                        >
                          {column.label}
                        </th>
                      ))}
                      <th className="w-24 whitespace-nowrap p-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr
                        key={item.id}
                        className="border-b border-outline-variant last:border-0"
                      >
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            className="whitespace-nowrap p-3"
                          >
                            {column.key === 'mapa_estabelecimento_cnpj'
                              ? (item.mapa_estabelecimento_cnpj
                                ? formatCNPJ(String(item.mapa_estabelecimento_cnpj))
                                : '-')
                              : formatValue(item[column.key])}
                          </td>
                        ))}
                        <td className="whitespace-nowrap p-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Ações"
                                disabled={saving}
                              >
                                <MoreHorizontalIcon />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-auto min-w-40"
                            >
                              <DropdownMenuGroup>
                                <DropdownMenuItem
                                  onSelect={() => {
                                    // defer: dropdown unmount must finish before dialog open, else modal dismisses
                                    setTimeout(() => openEdit(item), 0);
                                  }}
                                >
                                  Editar
                                </DropdownMenuItem>
                              </DropdownMenuGroup>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td
                          colSpan={columns.length + 1}
                          className="p-6 text-center text-on-surface-variant"
                        >
                          Nenhuma unidade encontrada
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between border-t border-outline-variant p-4">
                <Button
                  variant="outline"
                  disabled={skip === 0}
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                >
                  Anterior
                </Button>
                <span className="text-sm text-on-surface-variant">
                  Offset: {skip} | Limite: {limit}
                </span>
                <Button
                  variant="outline"
                  disabled={atEnd}
                  onClick={() => setSkip(skip + limit)}
                >
                  Próximo
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) closeForm();
        }}
        modal={true}
      >
        <DialogContent
          ref={setDialogContainer}
          className="sm:max-w-4xl"
        >
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Editar Unidade' : 'Nova Unidade'}</DialogTitle>
          </DialogHeader>

          <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            noValidate
          >
            <FieldGroup>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field data-invalid={!!errors.nome}>
                  <FieldLabel htmlFor="nome">Nome Unidade *</FieldLabel>
                  <Input
                    id="nome"
                    aria-invalid={!!errors.nome}
                    {...register('nome')}
                  />
                  <FieldError errors={errors.nome ? [errors.nome] : undefined} />
                </Field>

                <Field data-invalid={!!errors.mapa_estabelecimento_numero}>
                  <FieldLabel htmlFor="mapa_estabelecimento_numero">Mapa Estabelecimento Número *</FieldLabel>
                  <Input
                    id="mapa_estabelecimento_numero"
                    type="number"
                    aria-invalid={!!errors.mapa_estabelecimento_numero}
                    {...register('mapa_estabelecimento_numero')}
                  />
                  <FieldError errors={errors.mapa_estabelecimento_numero ? [errors.mapa_estabelecimento_numero] : undefined} />
                </Field>

                <Field data-invalid={!!errors.mapa_estabelecimento_ambito}>
                  <FieldLabel htmlFor="mapa_estabelecimento_ambito">Mapa Estabelecimento Âmbito *</FieldLabel>
                  <Controller
                    control={control}
                    name="mapa_estabelecimento_ambito"
                    render={({ field }) => (
                      <Select
                        value={field.value || undefined}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger
                          id="mapa_estabelecimento_ambito"
                          aria-invalid={!!errors.mapa_estabelecimento_ambito}
                        >
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectItem value="SIF">SIF</SelectItem>
                            <SelectItem value="ER">ER</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <FieldError errors={errors.mapa_estabelecimento_ambito ? [errors.mapa_estabelecimento_ambito] : undefined} />
                </Field>

                <Field data-invalid={!!errors.mapa_estabelecimento_cnpj}>
                  <FieldLabel htmlFor="mapa_estabelecimento_cnpj">Mapa Estabelecimento CNPJ *</FieldLabel>
                  <Controller
                    control={control}
                    name="mapa_estabelecimento_cnpj"
                    render={({ field }) => (
                      <Input
                        id="mapa_estabelecimento_cnpj"
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                        value={field.value}
                        onChange={(e) => field.onChange(formatCNPJ(e.target.value))}
                        onBlur={field.onBlur}
                        aria-invalid={!!errors.mapa_estabelecimento_cnpj}
                      />
                    )}
                  />
                  <FieldError errors={errors.mapa_estabelecimento_cnpj ? [errors.mapa_estabelecimento_cnpj] : undefined} />
                </Field>

                <Field data-invalid={!!errors.mapa_estabelecimento_uf}>
                  <FieldLabel htmlFor="mapa_estabelecimento_uf">Mapa Estabelecimento UF *</FieldLabel>
                  <Controller
                    control={control}
                    name="mapa_estabelecimento_uf"
                    render={({ field }) => (
                      <Combobox
                        items={estadosIBGE}
                        itemToStringLabel={(estado: IEstadoIBGE) => estado.sigla}
                        value={estadosIBGE.find((estado) => estado.sigla === field.value) ?? null}
                        onValueChange={(estado: IEstadoIBGE | null) => {
                          field.onChange(estado?.sigla ?? '');
                          setValue('mapa_estabelecimento_municipio_ibge', '');
                          setNomeMunicipioFallback(null);
                        }}
                        disabled={estadosLoading || estadosError}
                      >
                        <ComboboxInput
                          id="mapa_estabelecimento_uf"
                          placeholder="Selecione uma UF"
                          autoComplete="off"
                          aria-invalid={!!errors.mapa_estabelecimento_uf}
                        />
                        <ComboboxContent container={dialogContainer ?? undefined}>
                          <ComboboxEmpty>Nenhuma UF encontrada</ComboboxEmpty>
                          <ComboboxList>
                            {(estado: IEstadoIBGE) => (
                              <ComboboxItem
                                key={estado.sigla}
                                value={estado}
                              >
                                {estado.sigla}
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    )}
                  />
                  {estadosLoading && <FieldDescription>Carregando UFs...</FieldDescription>}
                  {estadosError && (
                    <FieldDescription
                      role="alert"
                      className="text-destructive"
                    >
                      Erro ao carregar UFs
                    </FieldDescription>
                  )}
                  <FieldError errors={errors.mapa_estabelecimento_uf ? [errors.mapa_estabelecimento_uf] : undefined} />
                </Field>

                <Field data-invalid={!!errors.mapa_estabelecimento_municipio_ibge}>
                  <FieldLabel htmlFor="mapa_estabelecimento_municipio_ibge">Mapa Estabelecimento Município IBGE *</FieldLabel>
                  <Controller
                    control={control}
                    name="mapa_estabelecimento_municipio_ibge"
                    render={({ field }) => (
                      <Combobox
                        items={municipiosIBGE}
                        itemToStringLabel={(municipio: IMunicipioIBGE) => municipio.nome}
                        value={municipiosIBGE.find((municipio) => String(municipio.id) === field.value) ?? null}
                        onValueChange={(municipio: IMunicipioIBGE | null) => {
                          field.onChange(municipio ? String(municipio.id) : '');
                          setNomeMunicipioFallback(municipio?.nome ?? null);
                        }}
                        disabled={!codUf || municipiosLoading || municipiosError}
                      >
                        <ComboboxInput
                          id="mapa_estabelecimento_municipio_ibge"
                          placeholder={codUf ? 'Selecione um município' : 'Selecione uma UF primeiro'}
                          autoComplete="off"
                          aria-invalid={!!errors.mapa_estabelecimento_municipio_ibge}
                        />
                        <ComboboxContent container={dialogContainer ?? undefined}>
                          <ComboboxEmpty>Nenhum município encontrado</ComboboxEmpty>
                          <ComboboxList>
                            {(municipio: IMunicipioIBGE) => (
                              <ComboboxItem
                                key={municipio.id}
                                value={municipio}
                              >
                                {municipio.nome}
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    )}
                  />
                  {municipiosLoading && <FieldDescription>Carregando municípios...</FieldDescription>}
                  {municipiosError && (
                    <FieldDescription
                      role="alert"
                      className="text-destructive"
                    >
                      Erro ao carregar municípios
                    </FieldDescription>
                  )}
                  <FieldError errors={errors.mapa_estabelecimento_municipio_ibge ? [errors.mapa_estabelecimento_municipio_ibge] : undefined} />
                </Field>

                <Field>
                  <FieldLabel htmlFor="nome_municipio_ibge">Nome do município</FieldLabel>
                  <Input
                    id="nome_municipio_ibge"
                    readOnly
                    value={
                      municipiosIBGE.find((municipio) => String(municipio.id) === codMunicipioIbge)?.nome
                      ?? nomeMunicipioFallback
                      ?? ''
                    }
                    placeholder="Selecione o município"
                  />
                </Field>

                <Field data-invalid={!!errors.mapa_username}>
                  <FieldLabel htmlFor="mapa_username">Mapa Usuário *</FieldLabel>
                  <Input
                    id="mapa_username"
                    autoComplete="off"
                    aria-invalid={!!errors.mapa_username}
                    {...register('mapa_username')}
                  />
                  <FieldError errors={errors.mapa_username ? [errors.mapa_username] : undefined} />
                </Field>

                <Field data-invalid={!!errors.mapa_password}>
                  <FieldLabel htmlFor="mapa_password">Mapa Senha *</FieldLabel>
                  <Input
                    id="mapa_password"
                    type="password"
                    autoComplete="off"
                    aria-invalid={!!errors.mapa_password}
                    {...register('mapa_password')}
                  />
                  <FieldError errors={errors.mapa_password ? [errors.mapa_password] : undefined} />
                </Field>

                <Field data-invalid={!!errors.smtp_host}>
                  <FieldLabel htmlFor="smtp_host">SMTP Host</FieldLabel>
                  <Input
                    id="smtp_host"
                    autoComplete="off"
                    {...register('smtp_host')}
                  />
                </Field>

                <Field data-invalid={!!errors.smtp_port}>
                  <FieldLabel htmlFor="smtp_port">SMTP Port</FieldLabel>
                  <Input
                    id="smtp_port"
                    type="number"
                    autoComplete="off"
                    {...register('smtp_port')}
                  />
                </Field>

                <Field data-invalid={!!errors.smtp_username}>
                  <FieldLabel htmlFor="smtp_username">SMTP Usuário</FieldLabel>
                  <Input
                    id="smtp_username"
                    autoComplete="off"
                    {...register('smtp_username')}
                  />
                </Field>

                <Field data-invalid={!!errors.smtp_password}>
                  <FieldLabel htmlFor="smtp_password">SMTP Password</FieldLabel>
                  <Input
                    id="smtp_password"
                    type="password"
                    autoComplete="off"
                    {...register('smtp_password')}
                  />
                </Field>

                <Field data-invalid={!!errors.email_from}>
                  <FieldLabel htmlFor="email_from">SMTP Email From</FieldLabel>
                  <Input
                    id="email_from"
                    type="email"
                    autoComplete="off"
                    {...register('email_from')}
                  />
                </Field>

                <Field orientation="horizontal">
                  <Controller
                    control={control}
                    name="smtp_use_tls"
                    render={({ field }) => (
                      <Checkbox
                        id="smtp_use_tls"
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                      />
                    )}
                  />
                  <FieldLabel
                    htmlFor="smtp_use_tls"
                    className="font-normal"
                  >
                    Usar TLS
                  </FieldLabel>
                </Field>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={saving || !isValid}
                >
                  {saving ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar'}
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancelar
                </Button>
              </div>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
